/**
 * Pure decision logic for the member page's inline "Expires" cell editor
 * (`set_member_package_expiry`).
 *
 * Extracted from `MemberDetail.tsx` for the same reason `pr-person-edit.ts`,
 * `session-note-edit.ts` and `inbox-matter-edit.ts` were: the root vitest run
 * collects `apps/**\/*.test.ts` but NOT `.test.tsx`, and the whole `web` tree is
 * ESLint-ignored and coverage-excluded. Logic left inside the component ships
 * entirely unverified — so everything here is a plain function over plain data,
 * and the component is left holding only state and markup.
 *
 * The one rule worth stating up front: the cell edits a CALENDAR DATE in the
 * STUDIO's timezone, never the browser's and never an instant. The stored
 * column is `timestamptz`, and the action resolves the date to end-of-day in
 * the studio's own zone server-side — so this module's only timezone job is to
 * turn the stored instant back into the date input's value, which it does
 * through `isoToZonedDateKey` rather than any `new Date(...)` arithmetic.
 */

import { isoToZonedDateKey } from "./studio-tz.js";

/** What the editor is holding while open. */
export interface ExpiryDraft {
  /** `<input type="date">` value — `YYYY-MM-DD`, or `''` when unset. */
  readonly date: string;
  /** The explicit "No expiry" choice, which is NOT the same as an empty date. */
  readonly noExpiry: boolean;
}

export type ParsedExpiry =
  | { readonly ok: true; readonly value: string | null }
  | { readonly ok: false; readonly message: string };

/**
 * The stored instant → the date input's initial value, in the STUDIO's calendar
 * day. A package expiring `2026-09-18T22:59:59Z` is the 18th in London and the
 * 18th in the input; the same instant is the 18th in New York too, but a
 * 23:30Z expiry would be the NEXT London day — which is exactly why this goes
 * through the zone-aware key rather than `iso.slice(0, 10)`.
 */
export function expiryIsoToDateInput(iso: string | null): string {
  if (iso === null || iso === "") return "";
  return isoToZonedDateKey(iso);
}

/**
 * The draft a freshly-opened editor starts from. A package with no expiry opens
 * with the "No expiry" box already ticked and an empty date, so the read view
 * and the edit view agree before anything is typed.
 */
export function initialExpiryDraft(currentIso: string | null): ExpiryDraft {
  return {
    date: expiryIsoToDateInput(currentIso),
    noExpiry: currentIso === null,
  };
}

/**
 * `true` when `ymd` is a `YYYY-MM-DD` string naming a date that actually
 * exists. Mirrors `isRealCalendarDate` in the canonical action — deliberately
 * duplicated rather than imported, because the SPA does not depend on
 * `@layer/canonical-actions` and a browser bundle should not start to. The
 * server check is the ENFORCEMENT (`expiry_date_invalid` at the chokepoint);
 * this one exists only so the operator sees the problem before a round-trip.
 *
 * `Date.UTC` with NUMERIC components — never `new Date("2026-02-31")`, whose
 * lenient parse would silently accept it as 3 March.
 */
export function isRealCalendarDate(ymd: string): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (m === null) return false;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  const probe = new Date(Date.UTC(year, month - 1, day));
  return (
    probe.getUTCFullYear() === year &&
    probe.getUTCMonth() === month - 1 &&
    probe.getUTCDate() === day
  );
}

/**
 * Draft → the wire value for `expires_on`: a `YYYY-MM-DD` string, or `null` for
 * never-expires. `null` is only ever produced by the EXPLICIT "No expiry" tick
 * — an empty date box is an error, never a silent clear. That distinction is
 * the whole reason the draft carries two fields instead of one: clearing an
 * expiry is an unbounded credit grant, and it should take a deliberate act.
 */
export function parseExpiryDraft(draft: ExpiryDraft): ParsedExpiry {
  if (draft.noExpiry) return { ok: true, value: null };
  const date = draft.date.trim();
  if (date === "") {
    return {
      ok: false,
      message: "Pick a date, or tick “No expiry” to remove the expiry.",
    };
  }
  if (!/^20\d{2}-\d{2}-\d{2}$/.test(date)) {
    return { ok: false, message: "Enter the date as YYYY-MM-DD." };
  }
  if (!isRealCalendarDate(date)) {
    // Never the date typed (field-errors, 2026-11-01): what is wrong with it.
    return {
      ok: false,
      message: "That date does not exist on the calendar — check the day and the month.",
    };
  }
  return { ok: true, value: date };
}

/**
 * `true` when saving would write the value the row already holds. Used to keep
 * Save inert rather than minting an audit row that records no change — a
 * re-picked identical date is the commonest way to open and close the editor.
 */
export function isExpiryUnchanged(
  currentIso: string | null,
  next: string | null,
): boolean {
  if (next === null) return currentIso === null;
  if (currentIso === null) return false;
  return expiryIsoToDateInput(currentIso) === next;
}

/**
 * The consequence line shown BEFORE the operator saves — the safety affordance
 * this whole feature turns on, and the reason the action is kept out of every
 * chat tier (a chat turn cannot show it before committing).
 *
 * `usablePackageSql` gates every booking surface on
 * `expires_at IS NULL OR expires_at > now()`, so moving a date across `now()`
 * moves real bookable credit in one write: prod holds packages with 69 unspent
 * sessions sitting behind a date in the past. The line states the direction and
 * the count; `null` means the edit does not cross the boundary and needs no
 * warning.
 *
 * `nowMs` is injected rather than read from the clock so this stays a pure
 * function (and so the tests are not time-of-day flaky).
 */
export function describeExpiryConsequence(args: {
  readonly currentIso: string | null;
  readonly next: string | null;
  readonly remainingSessions: number;
  readonly nowMs: number;
}): string | null {
  const { currentIso, next, remainingSessions, nowMs } = args;
  if (remainingSessions <= 0) return null;

  const wasUsable =
    currentIso === null || new Date(currentIso).getTime() > nowMs;
  // The action stores end-of-day, so "is the new date still live?" is decided
  // at the END of that day — picking today keeps the credits usable.
  const willBeUsable =
    next === null || new Date(`${next}T23:59:59.999Z`).getTime() > nowMs;

  const credits = `${remainingSessions} credit${remainingSessions === 1 ? "" : "s"}`;
  if (!wasUsable && willBeUsable) {
    return next === null
      ? `This makes ${credits} bookable again, with no expiry.`
      : `This makes ${credits} bookable again.`;
  }
  if (wasUsable && !willBeUsable) {
    return `This removes ${credits} from booking straight away.`;
  }
  if (wasUsable && next === null && currentIso !== null) {
    return `${credits} will never expire.`;
  }
  return null;
}
